import React, { useRef, useEffect, useMemo, useState } from "react";
import { Home, ChevronDown, Check, Crown, Users, Plus } from "lucide-react";
import { AddHomeModal } from "./modals/AddHomeModal";
import { cn } from "../lib/utils";
import { useProperty } from "../contexts/PropertyContext";

interface PropertySwitcherProps {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}

export function PropertySwitcher({ isOpen: controlledIsOpen, setIsOpen: controlledSetIsOpen }: PropertySwitcherProps = {}) {
  const { currentProperty, properties, switchProperty } = useProperty();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showAddHomeModal, setShowAddHomeModal] = useState(false);
  
  const isOpen = controlledIsOpen ?? false;
  const setIsOpen = useMemo(() => controlledSetIsOpen ?? (() => {}), [controlledSetIsOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, setIsOpen]);

  if (!currentProperty) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 h-10 pl-2.5 pr-3 rounded-lg border bg-background",
          "hover:bg-secondary transition-all duration-200",
          "text-sm font-medium"
        )}
      >
        {currentProperty.accessRole === 'family_member' ? (
          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <Home className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="max-w-[110px] truncate">{currentProperty.name}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform ml-0.5",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 right-0 min-w-[240px] z-50",
            "bg-background border rounded-lg shadow-lg",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
            "duration-200"
          )}
        >
          <div className="p-2">
            {properties.map((property) => {
              const isActive = currentProperty.id === property.id;
              return (
                <button
                  key={property.id}
                  onClick={() => {
                    switchProperty(property.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-md",
                    "transition-colors duration-200 text-left",
                    isActive
                      ? "bg-secondary"
                      : "hover:bg-secondary/50"
                  )}
                >
                  {property.accessRole === 'family_member' ? (
                    <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <Crown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{property.name}</span>
                      {property.accessRole === 'family_member' ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex-shrink-0">
                          Member
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0">
                          Owner
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {property.accessRole === 'family_member' && property.ownerName
                        ? `${property.ownerName}'s home`
                        : property.address}
                    </div>
                  </div>
                  {isActive && (
                    <Check className="h-4 w-4 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Add Home */}
          <div className="border-t px-2 py-2">
            <button
              onClick={() => {
                setIsOpen(false);
                setShowAddHomeModal(true);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-[#0277BD] hover:bg-blue-50 transition-colors duration-200"
            >
              <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Plus className="h-3.5 w-3.5 text-[#0277BD]" />
              </div>
              Add New Home
            </button>
          </div>
        </div>
      )}
      <AddHomeModal open={showAddHomeModal} onOpenChange={setShowAddHomeModal} />
    </div>
  );
}
